import Moment    from 'moment';
import { Mixin } from '$/lib/utils';
import { In }    from '$/lib/typeormExt';

import { Field, JSONable } from '$/entities/lib/JSONable';
import { EntityID }        from '$/entities/BaseEntity';
import { entity }          from '$/entities/runtimeEntities';
import type { File }       from '$/entities/File';

export enum ChatMessageRole {
	User      = 'user',
	Assistant = 'assistant',
}

/**
 * Represents a message in a chat with a chat bot.
 * Messages have a role indicating what type of message it is:
 * 	- User messages are from the user engaging in the chat
 * 	- Assistant messages are from the chat bot
 * Messages also can have an optional name, indicating more specifically who the sender is (e.g. landlord, tenant, etc.)
 * Finally, messages have content, which is the actual text of the message.
 */
@Mixin(JSONable)
export class ChatMessage {

	@Field()
	role: ChatMessageRole;

	@Field()
	message: string;

	@Field()
	name: string;

	@Field()
	createdOn: Date;

	@Field()
	completedOn: Date;

	@Field()
	files: EntityID[];

	@Field()
	isStreaming: boolean;

	constructor(initialValues: Partial<ChatMessage> = {}) {
		this.role        = initialValues.role;
		this.message     = initialValues.message?.trim() ?? '';
		this.name        = initialValues.name ?? '';
		this.createdOn   = Moment(initialValues.createdOn).toDate();
		this.completedOn = initialValues.completedOn ? Moment(initialValues.completedOn).toDate() : undefined;
		this.files       = initialValues.files ?? [];
		this.isStreaming = initialValues.isStreaming ?? false;
	}

	get isUser() {
		return this.role === ChatMessageRole.User;
	}

	get isAssistant() {
		return this.role === ChatMessageRole.Assistant;
	}

	get hasCitations() {
		return _.isNotEmpty(this.files);
	}

	// SHOULDDO: This is getting saved in the DB when it shouldn't be. Related to using JSON.parse instead of classToPlain in JSONable
	private _loadedFiles: File[] = [];
	get loadedFiles() {
		return this._loadedFiles;
	}

	toObject() {
		return { role : this.role, content : this.message };
	}

	async loadFiles() {
		this._loadedFiles = _.isEmpty(this.files) ? [] : await entity.File.find({ where : { id : In(this.files) } });
	}

	/**
	 * Helper function to create a new user message.
	 * Basically just does the same thing as the constructor, but slightly less verbose and without having to import ChatMessageRole.
	 * @param content The content of the message.
	 * @param initialValues The initial values for the other fields to set on the message.
	 * @returns The new user message.
	 */
	static newUserMessage(content: string, initialValues: Omit<Partial<ChatMessage>, 'role' | 'message'> = {}) {
		return new ChatMessage({ ...initialValues, role : ChatMessageRole.User, message : content });
	}

	/**
	 * Helper function to create a new assistant message.
	 * Basically just does the same thing as the constructor, but slightly less verbose and without having to import ChatMessageRole.
	 * @param content The content of the message.
	 * @param initialValues The initial values for the other fields to set on the message.
	 * @returns The new assistant message.
	 */
	static newAssistantMessage(content: string, initialValues: Omit<Partial<ChatMessage>, 'role' | 'message'> = {}) {
		return new ChatMessage({ ...initialValues, role : ChatMessageRole.Assistant, message : content });
	}

}
